import { useQueries } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import useGerarSchemaDinamico from 'hooks/useGerarSchemaDinamico';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Mascaras } from 'utils/formatUtils';
import { IOptions } from 'utils/types';
import { Campo } from 'utils/types/campo';
import {
  CamposNaoEditaveis,
  CamposNaoVisiveisAtualizacao,
  CamposNaoVisiveisInsercao,
  TipoCampo,
  TipoCampoSelecionavel as TiposCamposSelecionaveis,
} from 'utils/types/campo';
import { IModificadorCategoria } from 'utils/types/modificadorCategoria';

import { Grid2 as Grid } from '@mui/material';

import BackdropLoading from 'components/BackdropLoading';
import CustomAutocomplete from 'components/CustomAutocomplete/CustomAutocomplete';
import CustomDatePicker from 'components/CustomDatePicker';
import CustomInput from 'components/CustomInput';
import CustomSelect from 'components/CustomSelect';

import { getValoresCampoSelect } from './addEditRegistrosForm.service';

interface DadosCamposPorRegistro {
  campos: Campo[];
  valores: Record<string, any>;
}

interface Props {
  dados: DadosCamposPorRegistro;
  modo: 'add' | 'edit';
  registroCodigo: string;
  processamentoSpedId: string;
  onSubmit: (values: any) => void;
  modificadoresCategoriaBloco?: IModificadorCategoria[];
  modificadoresCategoriaRegistro?: IModificadorCategoria[];
  modificadorRegistroAbaAtual?: number | null;
}

export interface AddEditRegistrosFormHandle {
  submit: () => void;
}

export const calcularTamanhoGrid = (campo: Campo) => {
  const tamanhoMaximo = campo.tamanhoMaximo;
  if (isCampoSelecionavel(campo)) return 3;
  if (CamposNaoEditaveis.includes(campo.referencia)) return 1;
  if (tamanhoMaximo <= 2) return 1;
  if (tamanhoMaximo <= 14) return 2;
  if (tamanhoMaximo <= 20) return 3;
  if (tamanhoMaximo <= 40) return 4;
  if (tamanhoMaximo <= 60) return 5;
  if (tamanhoMaximo <= 80) return 6;
  return 7;
};

const isCampoSelecionavel = (campo: Campo) =>
  TiposCamposSelecionaveis.includes(campo.tipoCampo);

const AddEditRegistrosForm = forwardRef<AddEditRegistrosFormHandle, Props>(
  (
    {
      dados,
      modo,
      registroCodigo,
      processamentoSpedId,
      onSubmit,
      modificadoresCategoriaBloco,
      modificadoresCategoriaRegistro,
      modificadorRegistroAbaAtual,
    },
    ref,
  ) => {
    const isCampoDesabilitado = (campo: Campo) => {
      return (
        CamposNaoEditaveis.includes(campo.referencia.toUpperCase()) ||
        modificadoresCategoriaBloco?.some((categoria) =>
          categoria.campoIDs.includes(campo.id),
        ) ||
        modificadoresCategoriaRegistro?.some((categoria) =>
          categoria.campoIDs.includes(campo.id),
        )
      );
    };

    const isCampoVisivel = (referencia: string) =>
      (modo === 'add' &&
        !CamposNaoVisiveisInsercao.includes(referencia.toUpperCase())) ||
      (modo === 'edit' &&
        !CamposNaoVisiveisAtualizacao.includes(referencia.toUpperCase()));

    const { campos = [], valores: valoresBrutos = {} } = dados;

    const camposSelect = campos.filter((campo) => isCampoSelecionavel(campo));

    const selectQueries = useQueries({
      queries: useMemo(
        () =>
          camposSelect.map((campo) => ({
            queryKey: [
              'selectOptions',
              campo.tipoCampo,
              registroCodigo,
              campo.id,
            ],
            queryFn: () =>
              getValoresCampoSelect(
                registroCodigo.toString(),
                campo.id,
                processamentoSpedId,
              ),
            staleTime: 1000 * 60 * 10,
          })),
        [camposSelect, registroCodigo, processamentoSpedId],
      ),
    });

    const [isLoadingForm, setIsLoadingForm] = useState(false);
    const [selectOptionsMap, setSelectOptionsMap] = useState(
      new Map<number, IOptions[]>(),
    );
    const [valoresIniciais, setValoresIniciais] = useState<any>({});
    const jaSetouValoresRef = useRef(false);

    const selectStatuses = selectQueries.map((q) => q.status).join(',');

    useEffect(() => {
      const todasCompletas = selectQueries.every((q) => q.status === 'success');
      const algumaCarregando = selectQueries.some((q) => q.isLoading);

      setIsLoadingForm(algumaCarregando);

      if (!todasCompletas || jaSetouValoresRef.current) return;

      const novaMap = new Map<number, IOptions[]>();
      selectQueries.forEach((query, index) => {
        const campoId = camposSelect[index].id;
        const options =
          query.data?.map((opt) => ({
            label: `${opt.valor} - ${opt.descricao}`,
            value: opt.valor,
          })) ?? [];
        novaMap.set(campoId, options);
      });
      setSelectOptionsMap(novaMap);

      let valoresFormatados: any = {};
      const registro = { ...valoresBrutos.conteudo[0] };

      if (modo === 'edit' && valoresBrutos?.conteudo?.length) {
        Object.keys(registro).forEach((key) => {
          const campo = campos.find((c) => c.referencia === key);
          if (campo && isCampoSelecionavel(campo)) {
            const valorCampo = novaMap
              .get(campo.id)
              ?.find((option: IOptions) =>
                option.label.endsWith(registro[key]),
              );

            const valorOriginal = registro[key];
            if (valorCampo) {
              registro[key] = valorCampo.value;
            } else if (
              typeof valorOriginal === 'string' &&
              valorOriginal.includes(' - ')
            ) {
              registro[key] = valorOriginal.split(' - ').pop()?.trim() ?? '';
            } else {
              registro[key] = valorOriginal;
            }
          }
        });
        valoresFormatados = registro;
      } else if (modo === 'add') {
        Object.keys(registro).forEach((atributo) => {
          if (CamposNaoVisiveisInsercao.includes(atributo)) {
            delete registro[atributo];
          }
        });
        registro['REG'] = registroCodigo;
        modificadoresCategoriaBloco?.forEach((modificador) => {
          const camposModificador = campos.filter((c) =>
            modificador.campoIDs.includes(c.id),
          );
          if (camposModificador.length > 0) {
            camposModificador.forEach((campoModificador) => {
              registro[campoModificador?.referencia] = modificador.valor;
            });
          }
        });

        if (!!modificadorRegistroAbaAtual) {
          const modificadorAbaAtual = modificadoresCategoriaRegistro?.find(
            (modificador) => modificador.id === modificadorRegistroAbaAtual,
          );
          const camposModificador = campos.filter((c) =>
            modificadorAbaAtual?.campoIDs.includes(c.id),
          );
          camposModificador.forEach((campoModificador) => {
            registro[campoModificador?.referencia] = modificadorAbaAtual?.valor;
          });
        }
      }

      valoresFormatados = registro;
      Object.keys(valoresFormatados).forEach((key) => {
        const campo = campos.find((c) => c.referencia === key);

        if (campo?.tipoCampo === TipoCampo.number) {
          if (
            valoresFormatados[key] === '' ||
            valoresFormatados[key] === null
          ) {
            valoresFormatados[key] = null;
          } else if (typeof valoresFormatados[key] === 'number') {
            valoresFormatados[key] = valoresFormatados[key]
              .toString()
              .replace('.', ',');
          }
        }
      });
      setValoresIniciais(valoresFormatados);
      jaSetouValoresRef.current = true;
    }, [
      selectQueries,
      selectStatuses,
      campos,
      camposSelect,
      valoresBrutos,
      modo,
      registroCodigo,
      modificadoresCategoriaBloco,
      modificadoresCategoriaRegistro,
      modificadorRegistroAbaAtual,
    ]);

    const validationSchema = useGerarSchemaDinamico(campos);

    const formik = useFormik({
      initialValues: valoresIniciais,
      validationSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        onSubmit(values);
      },
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        formik.handleSubmit();
      },
    }));

    const camposOrdenados = [...campos].sort((a, b) => a.id - b.id);

    const renderCampo = (campo: Campo) => {
      const value = formik.values[campo.referencia] ?? '';
      const error = formik.errors[campo.referencia];
      const touched = formik.touched[campo.referencia];
      const helperText =
        touched && typeof error === 'string' ? error : undefined;
      const isErrored = touched && Boolean(error);

      const propsComuns = {
        id: campo.referencia,
        label: campo.descricao,
        value,
        required: campo.obrigatoriedade,
        name: campo.referencia,
        minLength: !campo.mascara ? campo.tamanhoMinimo : undefined,
        maxLength: !campo.mascara ? campo.tamanhoMaximo : undefined,
        mask: campo.mascara,
        error: isErrored ? true : undefined,
        helperText,
        disabled: isCampoDesabilitado(campo),
      };

      if (TiposCamposSelecionaveis.includes(campo.tipoCampo)) {
        const options = selectOptionsMap.get(campo.id) ?? [];
        const isLoading =
          selectQueries.find((q, i) => camposSelect[i].id === campo.id)
            ?.isLoading ?? false;

        const optionSelecionada = options.find((opt) => opt.value === value);

        if (options.length >= 20) {
          return (
            <CustomAutocomplete
              {...propsComuns}
              value={optionSelecionada || null}
              options={options}
              fullWidth
              setValue={(novoValor: IOptions | null) => {
                formik.setFieldValue(campo.referencia, novoValor?.value ?? '');
              }}
              disableCloseOnSelect
              freeSolo={false}
            />
          );
        } else {
          return (
            <CustomSelect
              {...propsComuns}
              value={value}
              options={options}
              loading={isLoading}
              setValue={(novoValor: any) =>
                formik.setFieldValue(campo.referencia, novoValor)
              }
            />
          );
        }
      }

      if (campo.tipoCampo === TipoCampo.date) {
        const isData = campo.mascara === Mascaras.data;
        const formato = isData ? 'DD/MM/YYYY' : 'MM/YYYY';
        const valorData: Dayjs | null = !!value
          ? isData
            ? dayjs(value)
            : dayjs(value, 'MMYYYY')
          : null;

        return (
          <CustomDatePicker
            {...propsComuns}
            format={formato}
            value={valorData}
            minDate={dayjs().subtract(5, 'year')}
            setValue={(novoValor: Dayjs | null) => {
              let valorFormatado: string | null = null;
              if (novoValor?.isValid()) {
                valorFormatado = isData
                  ? novoValor?.format('YYYY-MM-DD')
                  : novoValor?.format('MMYYYY');
              }
              formik.setFieldValue(campo.referencia, valorFormatado);
            }}
          />
        );
      }

      if (campo.tipoCampo === TipoCampo.number) {
        return (
          <CustomInput
            {...propsComuns}
            type="text"
            inputMode="decimal"
            onChange={(e) => {
              let valor = e.target.value;

              valor = valor.replace(/[^0-9,]/g, '');

              const partes = valor.split(',');
              if (partes.length > 2) {
                valor = partes[0] + ',' + partes[1];
              }

              if (valor.startsWith(',')) {
                valor = '';
              }

              formik.setFieldValue(
                campo.referencia,
                valor === '' ? null : valor,
              );
            }}
            onBlur={formik.handleBlur}
          />
        );
      }

      return (
        <CustomInput
          {...propsComuns}
          type="text"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      );
    };

    return (
      <form onSubmit={formik.handleSubmit}>
        {isLoadingForm && <BackdropLoading />}
        <Grid container spacing={2}>
          {camposOrdenados.map(
            (campo) =>
              isCampoVisivel(campo.referencia) && (
                <Grid
                  key={campo.id}
                  size={calcularTamanhoGrid(campo)}
                  mb={2}
                  mt={2}
                >
                  {renderCampo(campo)}
                </Grid>
              ),
          )}
        </Grid>
      </form>
    );
  },
);

export default AddEditRegistrosForm;
